<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';

defineProps({
  teammates: { type: Array, default: () => [] },
  isLoading: { type: Boolean, default: false },
});

const emit = defineEmits(['create']);

const { t } = useI18n();

const dialogRef = ref(null);
const name = ref('');
const description = ref('');
const memberIds = ref([]);

const v$ = useVuelidate({ name: { required } }, { name });

const hasMembers = computed(() => memberIds.value.length > 0);

const toggleMember = agentId => {
  memberIds.value = memberIds.value.includes(agentId)
    ? memberIds.value.filter(id => id !== agentId)
    : [...memberIds.value, agentId];
};

const resetForm = () => {
  name.value = '';
  description.value = '';
  memberIds.value = [];
  v$.value.$reset();
};

const open = () => dialogRef.value?.open();

const onSuccess = () => {
  resetForm();
  dialogRef.value?.close();
};

const handleConfirm = () => {
  v$.value.$touch();
  if (v$.value.$invalid || !hasMembers.value) return;

  emit('create', {
    name: name.value.trim(),
    description: description.value.trim(),
    memberIds: memberIds.value,
  });
};

defineExpose({ open, onSuccess });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="lg"
    overflow-y-auto
    :title="t('TEAM_PAGE.CREATE_TEAM_DIALOG.TITLE')"
    :confirm-button-label="t('TEAM_PAGE.CREATE_TEAM_DIALOG.SUBMIT')"
    :is-loading="isLoading"
    @confirm="handleConfirm"
    @close="resetForm"
  >
    <div class="flex flex-col gap-4">
      <Input
        v-model="name"
        :label="t('TEAM_PAGE.CREATE_TEAM_DIALOG.NAME_LABEL')"
        :placeholder="t('TEAM_PAGE.CREATE_TEAM_DIALOG.NAME_PLACEHOLDER')"
        :disabled="isLoading"
        :message="
          v$.name.$error ? t('TEAM_PAGE.CREATE_TEAM_DIALOG.NAME_ERROR') : ''
        "
        :message-type="v$.name.$error ? 'error' : 'info'"
        autofocus
        @input="v$.name.$touch"
      />
      <Input
        v-model="description"
        :label="t('TEAM_PAGE.CREATE_TEAM_DIALOG.DESCRIPTION_LABEL')"
        :placeholder="t('TEAM_PAGE.CREATE_TEAM_DIALOG.DESCRIPTION_PLACEHOLDER')"
        :disabled="isLoading"
      />

      <fieldset class="m-0 p-0 border-0">
        <legend class="mb-1.5 text-sm font-medium text-n-slate-12">
          {{ t('TEAM_PAGE.CREATE_TEAM_DIALOG.MEMBERS_LABEL') }}
        </legend>
        <p v-if="!teammates.length" class="m-0 text-sm text-n-slate-10">
          {{ t('TEAM_PAGE.CREATE_TEAM_DIALOG.MEMBERS_EMPTY') }}
        </p>
        <div
          v-else
          class="flex flex-col gap-0.5 p-1 overflow-y-auto max-h-52 rounded-lg outline outline-1 -outline-offset-1 outline-n-weak"
        >
          <label
            v-for="teammate in teammates"
            :key="teammate.id"
            class="flex items-center h-10 gap-2.5 px-2 mb-0 rounded-lg cursor-pointer hover:bg-n-alpha-1"
          >
            <Checkbox
              :model-value="memberIds.includes(teammate.id)"
              :disabled="isLoading"
              @change="toggleMember(teammate.id)"
            />
            <Avatar
              :name="teammate.name"
              :src="teammate.thumbnail"
              :size="24"
              rounded-full
            />
            <span class="flex-1 min-w-0 text-sm truncate text-n-slate-12">
              {{ teammate.name }}
            </span>
            <span class="text-xs shrink-0 text-n-slate-10">
              {{ teammate.roleLabel }}
            </span>
          </label>
        </div>
        <p
          v-if="v$.$dirty && !hasMembers && teammates.length"
          class="mt-1 mb-0 text-xs text-n-ruby-9"
        >
          {{ t('TEAM_PAGE.CREATE_TEAM_DIALOG.MEMBERS_ERROR') }}
        </p>
        <p
          v-else-if="teammates.length"
          class="mt-1 mb-0 text-xs text-n-slate-10"
        >
          {{
            t('TEAM_PAGE.CREATE_TEAM_DIALOG.SELECTED', {
              count: memberIds.length,
            })
          }}
        </p>
      </fieldset>
    </div>
  </Dialog>
</template>
